import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import db from '../../db/db';

const Ventas = () => {
    const navigate = useNavigate();
    const [ventas, setVentas] = useState([]);
    const [empleados, setEmpleados] = useState([]);
    const [metodos, setMetodos] = useState([]);
    const [eventos, setEventos] = useState([]);
    const [ventaACancelar, setVentaACancelar] = useState(null);
    const [statusCancelar, setStatusCancelar] = useState(false);

    const cargarVentas = useCallback(async () => {
        const sesion = await db.sesion.toArray();
        const sucursalId = sesion[0]?.sucursal?._id || sesion[0]?.sucursal;
        const [vs, emps, mets, evts] = await Promise.all([
            db.ventas.filter(v => String(v.sucursal?._id || v.sucursal) === String(sucursalId)).toArray(),
            db.empleados.toArray(),
            db.metodos.toArray(),
            db.eventos.toArray(),
        ]);
        setEmpleados(emps);
        setMetodos(mets);
        setEventos(evts);
        setVentas(vs.sort((a, b) => new Date(b.fecha) - new Date(a.fecha)));
    }, []);

    useEffect(() => { cargarVentas(); }, [cargarVentas]);

    const resolverMetodo = (id, lista) => {
        if (!id) return '—';
        const item = lista.find(x => x._id === id || String(x.id) === String(id));
        return item?.nombre || '—';
    };

    const resolverEmpleado = (id) => {
        if (!id) return '—';
        const emp = empleados.find(x => x._id === id || String(x.id) === String(id));
        if (!emp) return '—';
        return `${emp.nombre} ${emp.apellido_paterno || ''}`.trim();
    };

    const resolverEvento = (id, lista) => {
        if (!id) return '—';
        const item = lista.find(x => x._id === id || String(x.id) === String(id));
        return item?.nombre || '—';
    };

    const handleConfirmarCancelacion = async () => {
        if (!ventaACancelar) return;
        setStatusCancelar(true);
        try {
            await db.ventas.update(ventaACancelar.id, { status: false });
            await cargarVentas();
            setVentaACancelar(null);
        } finally {
            setStatusCancelar(false);
        }
    };

    const columns = [
        {
            name: 'Folio',
            selector: row => row.folio,
            sortable: true,
            grow: 0.5, 
            cell: row => (
                <span style={{ color: '#F5C800', fontWeight: 700, fontFamily: 'monospace' }}>
                    {row.folio || '—'}
                </span>
            ),
        },
        {
            name: 'Fecha',
            selector: row => row.fecha,
            sortable: true,
            grow: 1, 
            cell: row => {
                const d = new Date(row.fecha);
                return (
                    <span style={{ color: '#C0C0C0', fontSize: '0.85rem' }}>
                        {d.toLocaleDateString('es-MX')} {d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                );
            },
        },
        {
            name: 'Total',
            selector: row => row.total,
            sortable: true,
            grow: 0.5, 
            cell: row => (
                <span style={{ color: '#F0F0F0', fontWeight: 700 }}>
                    ${(row.total || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
            ),
        },
        {
            name: 'Status',
            selector: row => row.status,
            sortable: true,
            grow: 1, 
            cell: row => row.status ? (
                <span style={badges.realizada}>Realizada</span>
            ) : (
                <span style={badges.cancelada}>Cancelada</span>
            ),
        },
        {
            name: 'Método',
            selector: row => resolverMetodo(row.metodo, metodos),
            sortable: true,
            cell: row => <span style={{ color: '#C0C0C0' }}>{resolverMetodo(row.metodo, metodos)}</span>,
        },
        {
            name: 'Vendedor',
            selector: row => resolverEmpleado(row.vendedor),
            sortable: true,
            grow: 1, 
            cell: row => <span style={{ color: '#C0C0C0' }}>{resolverEmpleado(row.vendedor)}</span>,
        },
        {
            name: 'Evento',
            selector: row => resolverEvento(row.evento, eventos),
            sortable: true,
            grow: 1,
            cell: row => <span style={{ color: '#C0C0C0' }}>{resolverEvento(row.evento, eventos)}</span>,
        },
        {
            name: 'Precio',
            selector: row => row.cortesia ? 'cortesia' : row.especial ? 'especial' : '',
            sortable: true,
            grow: 0.8,
            cell: row => {
                if (row.cortesia) return <span style={badges.cortesia}>Cortesía</span>;
                if (row.especial) return <span style={badges.especial}>Especial</span>;
                return <span style={{ color: '#C0C0C0' }}>—</span>;
            },
        },
        {
            name: '',
            width: '110px',
            cell: row => (
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                    <button
                        style={styles.btnDetalle}
                        title="Ver detalle"
                        onClick={() => navigate(`/venta/${row.id}`)}
                    >
                        <i className="fas fa-eye"></i>
                    </button>
                    {row.status && (
                        <button
                            style={styles.btnCancelar}
                            title="Cancelar venta"
                            onClick={() => setVentaACancelar(row)}
                        >
                            <i className="fas fa-ban"></i>
                        </button>
                    )}
                </div>
            ),
        },
    ];

    return (
        <div style={styles.page}>
            <style>{`
                nav.rdt_Pagination button {
                    background: #1F1F1F !important;
                    border: 1px solid #2E2E2E !important;
                    border-radius: 8px !important;
                    color: #C0C0C0 !important;
                    margin: 0 2px !important;
                    min-width: 34px !important;
                    height: 34px !important;
                    cursor: pointer !important;
                    transition: border-color 0.15s, color 0.15s !important;
                }
                nav.rdt_Pagination button:hover:not(:disabled) {
                    border-color: #F5C800 !important;
                    color: #F5C800 !important;
                }
                nav.rdt_Pagination button:disabled {
                    opacity: 0.25 !important;
                    cursor: not-allowed !important;
                }
                nav.rdt_Pagination button svg {
                    fill: currentColor !important;
                }
            `}</style>
            <div style={styles.header}>
                <i className="fas fa-receipt me-3" style={{ color: '#F5C800', fontSize: '1.3rem' }}></i>
                <h4 style={styles.titulo}>Historial de ventas</h4>
            </div>

            <div style={styles.tableWrap}>
                <DataTable
                    columns={columns}
                    data={ventas}
                    pagination
                    paginationPerPage={15}
                    paginationRowsPerPageOptions={[10, 15, 25, 50]}
                    noDataComponent={
                        <div style={styles.vacio}>
                            <i className="fas fa-receipt" style={{ fontSize: '2rem', color: '#333', marginBottom: '0.75rem' }}></i>
                            <p style={{ color: '#888', margin: 0 }}>No hay ventas registradas</p>
                        </div>
                    }
                    customStyles={tableStyles}
                    defaultSortFieldId={2}
                    defaultSortAsc={false}
                    dense
                />
            </div>

            {/* Modal confirmación cancelación */}
            {ventaACancelar && (
                <div style={modal.overlay} onClick={() => !statusCancelar && setVentaACancelar(null)}>
                    <div style={modal.card} onClick={e => e.stopPropagation()}>
                        <div style={modal.icon}>
                            <i className="fas fa-ban" style={{ color: '#E53E3E', fontSize: '2rem' }}></i>
                        </div>
                        <h5 style={modal.titulo}>¿Cancelar venta?</h5>
                        <p style={modal.texto}>
                            La venta con folio <strong style={{ color: '#F5C800' }}>{ventaACancelar.folio}</strong> será marcada como cancelada.
                            Esta acción no se puede deshacer.
                        </p>
                        <div style={modal.botones}>
                            <button
                                style={modal.btnNo}
                                onClick={() => setVentaACancelar(null)}
                                disabled={statusCancelar}
                            >
                                No, mantener
                            </button>
                            <button
                                style={statusCancelar ? { ...modal.btnSi, opacity: 0.6, cursor: 'not-allowed' } : modal.btnSi}
                                onClick={handleConfirmarCancelacion}
                                disabled={statusCancelar}
                            >
                                {statusCancelar ? (
                                    <><span className="spinner-border spinner-border-sm me-2" role="status" />Cancelando...</>
                                ) : (
                                    'Sí, cancelar'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const badges = {
    cortesia: {
        background: 'rgba(72,187,120,0.1)',
        color: '#48BB78',
        border: '1px solid rgba(72,187,120,0.3)',
        borderRadius: '20px',
        padding: '0.2rem 0.65rem',
        fontSize: '0.78rem',
        fontWeight: 700,
    },
    especial: {
        background: 'rgba(167,139,250,0.1)',
        color: '#A78BFA',
        border: '1px solid rgba(167,139,250,0.3)',
        borderRadius: '20px',
        padding: '0.2rem 0.65rem',
        fontSize: '0.78rem',
        fontWeight: 700,
    },
    realizada: {
        background: 'rgba(245,200,0,0.12)',
        color: '#F5C800',
        border: '1px solid rgba(245,200,0,0.3)',
        borderRadius: '20px',
        padding: '0.2rem 0.65rem',
        fontSize: '0.78rem',
        fontWeight: 700,
    },
    cancelada: {
        background: 'rgba(229,62,62,0.1)',
        color: '#E53E3E',
        border: '1px solid rgba(229,62,62,0.3)',
        borderRadius: '20px',
        padding: '0.2rem 0.65rem',
        fontSize: '0.78rem',
        fontWeight: 700,
    },
};

const styles = {
    page: {
        maxWidth: '1400px',
        margin: '0 auto',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '1.5rem',
        paddingBottom: '1rem',
        borderBottom: '1px solid #2E2E2E',
    },
    titulo: {
        color: '#F0F0F0',
        fontWeight: 700,
        margin: 0,
    },
    tableWrap: {
        background: '#1A1A1A',
        border: '1px solid #2E2E2E',
        borderRadius: '14px',
        overflow: 'hidden',
    },
    vacio: {
        padding: '3rem',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    btnDetalle: {
        background: 'transparent',
        border: '1px solid #2E2E2E',
        borderRadius: '7px',
        padding: '0.3rem 0.55rem',
        color: '#F5C800',
        fontSize: '0.85rem',
        cursor: 'pointer',
    },
    btnCancelar: {
        background: 'transparent',
        border: '1px solid #2E2E2E',
        borderRadius: '7px',
        padding: '0.3rem 0.55rem',
        color: '#555',
        fontSize: '0.85rem',
        cursor: 'pointer',
        transition: 'color 0.15s, border-color 0.15s',
    },
};

const modal = {
    overlay: {
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '1rem',
    },
    card: {
        background: '#1A1A1A',
        border: '1px solid #2E2E2E',
        borderRadius: '16px',
        padding: '2rem',
        maxWidth: '420px',
        width: '100%',
        textAlign: 'center',
    },
    icon: {
        marginBottom: '1rem',
    },
    titulo: {
        color: '#F0F0F0',
        fontWeight: 700,
        marginBottom: '0.75rem',
    },
    texto: {
        color: '#888',
        fontSize: '0.95rem',
        marginBottom: '1.5rem',
        lineHeight: 1.6,
    },
    botones: {
        display: 'flex',
        gap: '0.75rem',
    },
    btnNo: {
        flex: 1,
        background: 'transparent',
        border: '1px solid #2E2E2E',
        borderRadius: '10px',
        padding: '0.8rem',
        color: '#888',
        fontWeight: 700,
        fontSize: '0.95rem',
        cursor: 'pointer',
    },
    btnSi: {
        flex: 1,
        background: '#E53E3E',
        border: 'none',
        borderRadius: '10px',
        padding: '0.8rem',
        color: '#fff',
        fontWeight: 800,
        fontSize: '0.95rem',
        cursor: 'pointer',
    },
};

const tableStyles = {
    table: { style: { background: '#1A1A1A' } },
    headRow: { style: { background: '#111111', borderBottomColor: '#2E2E2E' } },
    headCells: { style: { color: '#888888', fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' } },
    rows: { style: { background: '#1A1A1A', borderBottomColor: '#222222', minHeight: '52px', '&:hover': { background: '#1F1F1F' } } },
    pagination: { style: { background: '#111111', borderTopColor: '#2E2E2E', color: '#888' } },
    noData: { style: { background: '#1A1A1A' } },
};

export default Ventas;
